import { ComponentProps, FC } from "react";
import { Card, Typography, Row, Col, Progress } from "antd";
import { PlusCircleOutlined } from "@ant-design/icons";

import { Button } from "../../../../elements";
import Trend from "../Trend/Trend";
import styles from "./CorrelationMetric.module.less";

export enum CorrelationType {
  positive = "positive",
  negative = "negative",
  zero = "zero"
}

const { Text } = Typography;

export interface CorrelationMetricProps extends ComponentProps<typeof Card> {
  /**
   * type of correlation to show as an icon
   */
  correlationType: CorrelationType;
  /**
   * correlation trend
   */
  trend: string;
  /**
   * Correlation strength
   */
  correlation: string;
  /**
   * Correlation %
   */
  correlationPercent: number;
}

/**
 * Correlation metric component for comparing charts
 * @param {CorrelationType} correlationType
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} title
 * @param {Array<number>} metrics
 * @param {string} trend
 * @param {string} correlation
 * @param {%} correlationPercent
 * @param {string | undefined} className
 * @param {Pick<CorrelationMetricProps & {children?: React.ReactNode | undefined}, "prefixCls" | "extra" | "bordered" | "headStyle" | "bodyStyle" | "style" | "loading" | "hoverable" | "children" | "id" | "size" | "type" | "cover" | "actions" | "tabList" | "tabBarExtraContent" | "onTabChange" | "activeTabKey" | "defaultActiveTabKey" | "tabProps" | "defaultChecked" | "defaultValue" | "suppressContentEditableWarning" | "suppressHydrationWarning" | "accessKey" | "contentEditable" | "contextMenu" | "dir" | "draggable" | "hidden" | "lang" | "placeholder" | "slot" | "spellCheck" | "tabIndex" | "translate" | "radioGroup" | "role" | "about" | "datatype" | "inlist" | "prefix" | "property" | "resource" | "typeof" | "vocab" | "autoCapitalize" | "autoCorrect" | "autoSave" | "color" | "itemProp" | "itemScope" | "itemType" | "itemID" | "itemRef" | "results" | "security" | "unselectable" | "inputMode" | "is" | "aria-activedescendant" | "aria-atomic" | "aria-autocomplete" | "aria-busy" | "aria-checked" | "aria-colcount" | "aria-colindex" | "aria-colspan" | "aria-controls" | "aria-current" | "aria-describedby" | "aria-details" | "aria-disabled" | "aria-dropeffect" | "aria-errormessage" | "aria-expanded" | "aria-flowto" | "aria-grabbed" | "aria-haspopup" | "aria-hidden" | "aria-invalid" | "aria-keyshortcuts" | "aria-label" | "aria-labelledby" | "aria-level" | "aria-live" | "aria-modal" | "aria-multiline" | "aria-multiselectable" | "aria-orientation" | "aria-owns" | "aria-placeholder" | "aria-posinset" | "aria-pressed" | "aria-readonly" | "aria-relevant" | "aria-required" | "aria-roledescription" | "aria-rowcount" | "aria-rowindex" | "aria-rowspan" | "aria-selected" | "aria-setsize" | "aria-sort" | "aria-valuemax" | "aria-valuemin" | "aria-valuenow" | "aria-valuetext" | "dangerouslySetInnerHTML" | "onCopy" | "onCopyCapture" | "onCut" | "onCutCapture" | "onPaste" | "onPasteCapture" | "onCompositionEnd" | "onCompositionEndCapture" | "onCompositionStart" | "onCompositionStartCapture" | "onCompositionUpdate" | "onCompositionUpdateCapture" | "onFocus" | "onFocusCapture" | "onBlur" | "onBlurCapture" | "onChange" | "onChangeCapture" | "onBeforeInput" | "onBeforeInputCapture" | "onInput" | "onInputCapture" | "onReset" | "onResetCapture" | "onSubmit" | "onSubmitCapture" | "onInvalid" | "onInvalidCapture" | "onLoad" | "onLoadCapture" | "onError" | "onErrorCapture" | "onKeyDown" | "onKeyDownCapture" | "onKeyPress" | "onKeyPressCapture" | "onKeyUp" | "onKeyUpCapture" | "onAbort" | "onAbortCapture" | "onCanPlay" | "onCanPlayCapture" | "onCanPlayThrough" | "onCanPlayThroughCapture" | "onDurationChange" | "onDurationChangeCapture" | "onEmptied" | "onEmptiedCapture" | "onEncrypted" | "onEncryptedCapture" | "onEnded" | "onEndedCapture" | "onLoadedData" | "onLoadedDataCapture" | "onLoadedMetadata" | "onLoadedMetadataCapture" | "onLoadStart" | "onLoadStartCapture" | "onPause" | "onPauseCapture" | "onPlay" | "onPlayCapture" | "onPlaying" | "onPlayingCapture" | "onProgress" | "onProgressCapture" | "onRateChange" | "onRateChangeCapture" | "onSeeked" | "onSeekedCapture" | "onSeeking" | "onSeekingCapture" | "onStalled" | "onStalledCapture" | "onSuspend" | "onSuspendCapture" | "onTimeUpdate" | "onTimeUpdateCapture" | "onVolumeChange" | "onVolumeChangeCapture" | "onWaiting" | "onWaitingCapture" | "onAuxClick" | "onAuxClickCapture" | "onClick" | "onClickCapture" | "onContextMenu" | "onContextMenuCapture" | "onDoubleClick" | "onDoubleClickCapture" | "onDrag" | "onDragCapture" | "onDragEnd" | "onDragEndCapture" | "onDragEnter" | "onDragEnterCapture" | "onDragExit" | "onDragExitCapture" | "onDragLeave" | "onDragLeaveCapture" | "onDragOver" | "onDragOverCapture" | "onDragStart" | "onDragStartCapture" | "onDrop" | "onDropCapture" | "onMouseDown" | "onMouseDownCapture" | "onMouseEnter" | "onMouseLeave" | "onMouseMove" | "onMouseMoveCapture" | "onMouseOut" | "onMouseOutCapture" | "onMouseOver" | "onMouseOverCapture" | "onMouseUp" | "onMouseUpCapture" | "onSelect" | "onSelectCapture" | "onTouchCancel" | "onTouchCancelCapture" | "onTouchEnd" | "onTouchEndCapture" | "onTouchMove" | "onTouchMoveCapture" | "onTouchStart" | "onTouchStartCapture" | "onPointerDown" | "onPointerDownCapture" | "onPointerMove" | "onPointerMoveCapture" | "onPointerUp" | "onPointerUpCapture" | "onPointerCancel" | "onPointerCancelCapture" | "onPointerEnter" | "onPointerEnterCapture" | "onPointerLeave" | "onPointerLeaveCapture" | "onPointerOver" | "onPointerOverCapture" | "onPointerOut" | "onPointerOutCapture" | "onGotPointerCapture" | "onGotPointerCaptureCapture" | "onLostPointerCapture" | "onLostPointerCaptureCapture" | "onScroll" | "onScrollCapture" | "onWheel" | "onWheelCapture" | "onAnimationStart" | "onAnimationStartCapture" | "onAnimationEnd" | "onAnimationEndCapture" | "onAnimationIteration" | "onAnimationIterationCapture" | "onTransitionEnd" | "onTransitionEndCapture" | "ref" | "key">} rest
 * @return {JSX.Element}
 * @constructor
 */
const CorrelationMetric: FC<CorrelationMetricProps> & { CorrelationType: typeof CorrelationType } = ({
  correlationType,
  correlationPercent,
  title,
  className,
  onClick,
  trend,
  correlation,
  extra
}) => {
  return (
    <Row align="middle" className={className} onClick={onClick}>
      <Col xs={2} lg={1}>
        <Button
          size="large"
          type="link"
          data-gtm-category="Configurator"
          data-gtm-action="Add correlated chart"
          data-gtm-info={title}
        >
          <PlusCircleOutlined />
        </Button>
      </Col>
      <Col xs={6} lg={10}>
        <Text>{title}</Text>
      </Col>
      <Col xs={4} lg={3}>
        <Trend correlationType={correlationType} text={trend} />
      </Col>
      <Col xs={6} lg={4}>
        <Progress className={styles.Progress} steps={3} percent={correlationPercent} showInfo={false} />
        {correlation}
      </Col>
      <Col flex={1}>{extra}</Col>
    </Row>
  );
};

CorrelationMetric.CorrelationType = CorrelationType;

export default CorrelationMetric;
